import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import toast from "react-hot-toast";
import {
  retrieveAllAdminCostRatesApi,
  createAdminCostRateApi,
  updateAdminCostRateApi,
  deleteAdminCostRateApi,
} from "../../../components/api/AdminCostRateApiService";

// ─── Add / Edit dialog ────────────────────────────────────────────────────────

function AdminCostRateDialog({ open, onClose, onSaved, editRow }) {
  const isEdit = !!editRow;
  const [year, setYear]       = useState("");
  const [rate, setRate]       = useState("");
  const [remark, setRemark]   = useState("");
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setYear(String(editRow.year));
      setRate(String(editRow.rateEurPerKg));
      setRemark(editRow.remark || "");
    } else {
      setYear(String(new Date().getFullYear()));
      setRate("");
      setRemark("");
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    const y = parseInt(year, 10);
    const r = parseFloat(rate);
    if (!y || y < 2000 || y > 2100) { toast.error("Enter a valid year."); return; }
    if (!r || r <= 0)                { toast.error("Enter a valid rate > 0."); return; }

    setSaving(true);
    try {
      const payload = { year: y, rateEurPerKg: r, remark: remark || null };
      if (isEdit) {
        await updateAdminCostRateApi(editRow.id, payload);
        toast.success("Rate updated.");
      } else {
        await createAdminCostRateApi(payload);
        toast.success("Rate added.");
      }
      onSaved();
      onClose();
    } catch (err) {
      const msg = err?.response?.data;
      toast.error(typeof msg === "string" ? msg : "Could not save rate.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{isEdit ? "Edit Admin Cost Rate" : "Add Admin Cost Rate"}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth margin="dense" size="small" label="Year"
          value={year} onChange={(e) => setYear(e.target.value)}
          type="number" inputProps={{ min: 2000, max: 2100, step: 1 }}
        />
        <TextField
          fullWidth margin="dense" size="small" label="Rate (EUR/kg)"
          value={rate} onChange={(e) => setRate(e.target.value)}
          type="number" inputProps={{ min: 0, step: 0.0001 }}
        />
        <TextField
          fullWidth margin="dense" size="small" label="Remark (optional)"
          value={remark} onChange={(e) => setRemark(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main view ────────────────────────────────────────────────────────────────

export default function AdminCostRateView() {
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRow, setEditRow]   = useState(null);

  const load = () => {
    setLoading(true);
    retrieveAllAdminCostRatesApi()
      .then((res) => setRows(res?.data || []))
      .catch(() => toast.error("Could not load admin cost rates."))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete rate for ${row.year}?`)) return;
    try {
      await deleteAdminCostRateApi(row.id);
      toast.success("Deleted.");
      load();
    } catch {
      toast.error("Could not delete.");
    }
  };

  const openAdd  = () => { setEditRow(null); setDialogOpen(true); };
  const openEdit = (row) => { setEditRow(row); setDialogOpen(true); };

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={2} sx={{ borderRadius: 2 }}>
        <Toolbar sx={{ pl: 2, pr: 1 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>
            Admin Cost Rates (EUR / kg)
          </Typography>
          <Tooltip title="Add new year rate">
            <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd} size="small">
              Add
            </Button>
          </Tooltip>
        </Toolbar>

        {loading ? (
          <Box sx={{ p: 3, textAlign: "center" }}><CircularProgress /></Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                  <TableCell><strong>Year</strong></TableCell>
                  <TableCell align="right"><strong>Rate (EUR/kg)</strong></TableCell>
                  <TableCell><strong>Remark</strong></TableCell>
                  <TableCell><strong>Last Modified</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ color: "text.secondary", py: 4 }}>
                      No rates defined yet. Add a rate for each year.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell><strong>{row.year}</strong></TableCell>
                      <TableCell align="right">{Number(row.rateEurPerKg).toFixed(4)}</TableCell>
                      <TableCell sx={{ color: "text.secondary" }}>{row.remark || "—"}</TableCell>
                      <TableCell sx={{ color: "text.secondary", fontSize: "0.75rem" }}>
                        {row.modifiedAt
                          ? new Date(row.modifiedAt).toLocaleDateString()
                          : row.createdAt
                          ? new Date(row.createdAt).toLocaleDateString()
                          : "—"}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEdit(row)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => handleDelete(row)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <AdminCostRateDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={load}
        editRow={editRow}
      />
    </Box>
  );
}
